import React, {useState, useEffect, useMemo} from 'react';
import {
  View,
  StyleSheet,
  useWindowDimensions,
  Platform,
  FlatList,
} from 'react-native';
import {useSelector} from 'react-redux';
import DoubleClick from 'react-native-double-tap';
import {hasNotch, isTablet} from 'react-native-device-info';
import Orientation from 'react-native-orientation-locker';

import RemoteVideo from './RemoteVideo';
import {
  getRemoteVideoConsumers,
  resolveRemoteVideoSelection,
  setRemoteVideoFocusQuality,
} from './remoteVideoUtils';
import {getCompactVideoStyle} from '../videoLayout';

const RemoteVideos = () => {
  const consumers = useSelector((state) => state.communication.consumers);
  const incomingVideoEnabled = useSelector(
    (state) => state.meeting.settings.incomingVideoEnabled,
  );
  const roomClient = useSelector(
    (state) => state.communication.control.roomClient,
  );

  const videoConsumersArray = useMemo(
    () => getRemoteVideoConsumers(consumers),
    [consumers],
  );

  const [isVideoFocused, setIsVideoFocused] = useState(false);
  const [isVideoSelectMode, setIsVideoSelectMode] = useState(false);
  const windowWidth = useWindowDimensions().width;
  const windowHeight = useWindowDimensions().height;

  const compactVideoContainerStyle = getCompactVideoStyle(
    Platform.OS,
    isTablet(),
  );
  let videoContainerStyle = compactVideoContainerStyle;
  if (isVideoFocused && Platform.OS === 'web') {
    videoContainerStyle = styles.focusedWebVideoContainer;
  } else if (isVideoFocused) {
    videoContainerStyle = {
      width: windowWidth,
      height: windowHeight,
    };
  }

  const isAtLeastOneVideo = videoConsumersArray.length >= 1;
  const isMultipleVideos = videoConsumersArray.length >= 2;

  const [currentSelectedVideoId, setCurrentSelectedVideoId] = useState(null);
  const resolvedSelectedVideoId = resolveRemoteVideoSelection(
    videoConsumersArray,
    currentSelectedVideoId,
  );
  const currentSelectedVideo =
    videoConsumersArray.find(
      (videoConsumer) => videoConsumer.id === resolvedSelectedVideoId,
    ) || null;
  const selectedVideoId = currentSelectedVideo?.id || null;
  const selectedVideoType = currentSelectedVideo?.type || null;
  const selectedSpatialLayers = currentSelectedVideo?.spatialLayers || 1;
  const selectedTemporalLayers = currentSelectedVideo?.temporalLayers || 1;

  useEffect(() => {
    if (currentSelectedVideoId !== resolvedSelectedVideoId) {
      setCurrentSelectedVideoId(resolvedSelectedVideoId);
      setIsVideoFocused(false);
      setIsVideoSelectMode(false);
    }
  }, [currentSelectedVideoId, resolvedSelectedVideoId]);

  useEffect(() => {
    if (!isVideoFocused || !roomClient || !selectedVideoId) {
      return undefined;
    }

    const focusedConsumer = {
      id: selectedVideoId,
      type: selectedVideoType,
      spatialLayers: selectedSpatialLayers,
      temporalLayers: selectedTemporalLayers,
    };
    setRemoteVideoFocusQuality(roomClient, focusedConsumer, true);

    return () => {
      setRemoteVideoFocusQuality(roomClient, focusedConsumer, false);
    };
  }, [
    isVideoFocused,
    roomClient,
    selectedSpatialLayers,
    selectedTemporalLayers,
    selectedVideoId,
    selectedVideoType,
  ]);

  const toggleVideoFocused = (videoConsumer) => {
    setIsVideoFocused(!isVideoFocused);
    setCurrentSelectedVideoId(videoConsumer.id);
  };

  const setSelectedVideo = (videoConsumer) => {
    setIsVideoSelectMode(!isVideoSelectMode);
    setCurrentSelectedVideoId(videoConsumer.id);
  };

  const renderVideo = ({item}) => (
    <DoubleClick
      key={item.id}
      doubleTap={() => toggleVideoFocused(item)}
      singleTap={() => setSelectedVideo(item)}>
      <RemoteVideo
        remoteVideoConsumer={item}
        videoContainerStyle={videoContainerStyle}
        isFocused={isVideoFocused && resolvedSelectedVideoId === item.id}
        onToggleFocus={() => toggleVideoFocused(item)}
      />
    </DoubleClick>
  );

  const renderMultipleVideosScrollView = () => (
    <FlatList
      data={videoConsumersArray}
      renderItem={renderVideo}
      keyExtractor={(videoConsumer) => videoConsumer.id}
      horizontal
    />
  );

  const renderMultipleVideoStackIndicator = () => {
    if (
      !isVideoFocused &&
      isMultipleVideos &&
      isAtLeastOneVideo &&
      !isVideoSelectMode &&
      incomingVideoEnabled
    ) {
      return (
        <>
          <View
            style={[
              styles.videoStackRightContainer,
              {height: compactVideoContainerStyle.height},
            ]}
          />
          <View
            style={[
              styles.videoStackBottomContainer,
              {
                width: compactVideoContainerStyle.width - 10,
                left: -compactVideoContainerStyle.width,
              },
            ]}
          />
        </>
      );
    } else {
      return null;
    }
  };

  const isMultipleVideosMode =
    isVideoSelectMode &&
    isMultipleVideos &&
    !isVideoFocused &&
    incomingVideoEnabled;

  const isSingleVideoMode =
    (!isVideoSelectMode || isVideoFocused) &&
    isAtLeastOneVideo &&
    currentSelectedVideo &&
    incomingVideoEnabled;

  const [currentOrientation, setCurrentOrientation] = useState(
    Orientation.getInitialOrientation(),
  );

  useEffect(() => {
    const onOrientationDidChange = (orientation) => {
      if (currentOrientation !== orientation) {
        setCurrentOrientation(orientation);
      }
    };
    Orientation.addOrientationListener(onOrientationDidChange);

    return () => {
      Orientation.removeOrientationListener(onOrientationDidChange);
    };
  }, [currentOrientation]);

  const remoteVideoContainerTopPosition =
    currentOrientation !== 'PORTRAIT' ? 0 : 40;
  const remoteVideoContainerLeftPosition =
    currentOrientation === 'LANDSCAPE-LEFT' && hasNotch() ? 33 : 0;

  return (
    <View
      style={[
        styles.container,
        {
          top: isVideoFocused ? 0 : remoteVideoContainerTopPosition,
          left: isVideoFocused ? 0 : remoteVideoContainerLeftPosition,
        },
      ]}
      pointerEvents="box-none">
      {isMultipleVideosMode ? renderMultipleVideosScrollView() : null}
      {isSingleVideoMode ? (
        <DoubleClick
          doubleTap={() => toggleVideoFocused(currentSelectedVideo)}
          singleTap={() => {
            if (isMultipleVideos) {
              setIsVideoSelectMode(!isVideoSelectMode);
            }
          }}>
          <RemoteVideo
            key={currentSelectedVideo.id}
            remoteVideoConsumer={currentSelectedVideo}
            videoContainerStyle={videoContainerStyle}
            isFocused={isVideoFocused}
            onToggleFocus={() => toggleVideoFocused(currentSelectedVideo)}
          />
        </DoubleClick>
      ) : null}
      {renderMultipleVideoStackIndicator()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-end',
    position: 'absolute',
    backgroundColor: '#000000',
  },
  focusedWebVideoContainer: {
    width: '100vw',
    height: '100vh',
  },
  videoStackRightContainer: {
    width: 10,
    backgroundColor: '#FFFFFF50',
    top: 10,
  },
  videoStackBottomContainer: {
    height: 10,
    backgroundColor: '#FFFFFF50',
    top: 10,
  },
});

export default RemoteVideos;
